import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Alert, Spinner, Input, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import * as XLSX from 'xlsx';
import FileModal from '../components/Common/importExportModal';
import TransferModal from '../components/TransferModal';
import { currencies, groupedAssetStatusOptions } from '../constants/utils';
import PermissionGuard from '../components/Common/PermissionGuard';

const Assets = () => {
    document.title = "Assets | ArcMedix";
    const [organizations, setOrganizations] = useState(null);
    const [selectedLocationSubLocations, setSelectedLocationSubLocations] = useState([]);
    const [organizationId, setOrganizationId] = useState('');
    const [locations, setLocations] = useState([]);
    const [assets, setAssets] = useState([]);
    const [modal_large, setmodal_large] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [departments, setDepartments] = useState([]);
    const [manufacturer, setManufacturer] = useState([]);
    const [criticals, setCriticals] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [enumData, setEnumData] = useState([]);
    const [apiError, setApiError] = useState('');
    const [imageViewerModal, setImageViewerModal] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [qrModal, setQrModal] = useState(false);
    const [fileModal, setFileModal] = useState(false);
    const [transferModal, setTransferModal] = useState(false);
    const [qrGenerationSuccess, setQrGenerationSuccess] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        index: null,
        imageUrl: '',
        deleting: false
    });
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({
        department: '',
        location: '',
        manufacturer: ''
    });

    // New states for asset selection
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const fetchOrganizations = async() => {
        const {data} = await api.get('organization/all');
        setOrganizations(data.response);
        if (data.response && data.response.length > 0) {
            setOrganizationId(data.response[0].id);
        }
    };

    const fetchLocations = async() => {
        const {data} = await api.get(`organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999`)
        setLocations(data.response[0]?.locations);
    };
     const fetchManufacturer = async() => {
        const {data} = await api.get(`asset/get-manufacturer`)
        setManufacturer(data.response?.manufacturers);
    };

    const fetchVendors = async() => {
        try {
            const {data} = await api.get(`vendor/getAllVendors`);
            setVendors(data.response || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            setVendors([]);
        }
    };

    const fetchCriticality = async () => {
        try {
            const { data } = await api.get("dynamic-enum/find-by-name?name=criticality");
            setCriticals(data.response.values)
        } catch (error) {
            console.error("Error fetching enums:", error);
            return null;
        }
    };

    const fetchEnums = async () => {
        try {
          const { data } = await api.get("dynamic-enum/get-all");
          setEnumData(data.response || []);
        } catch (error) {
          console.error("Error fetching enums:", error);
        }
      };

    const handleSearch = useCallback((e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setAssets([]);
        setCount(10);
        setHasMore(true);
        // Clear selections when searching
        setSelectedAssets([]);
        setSelectAll(false);
    }, []);

    const fetchAssetFromOrganization = useCallback(async () => {
        if (!organizationId) return;
        
        setLoading(true);
        try {
            const params = {
                organizationId,
                count,
                search: searchTerm,
                currentLocation: filters.location,
                assetCategory: filters.department,
                manufacturer: filters.manufacturer
            };

            const {data} = await api.get(`asset/all`, { params });
            setAssets(data?.response[0]?.assets || []);
            
            if (data.response[0].assets.length < count) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

            // Clear selections when assets change
            setSelectedAssets([]);
            setSelectAll(false);
        } catch (error) {
            console.error('Error fetching assets:', error);
        } finally {
            setLoading(false);
        }
    }, [organizationId, count, searchTerm, filters]);

    // Handle individual asset selection
    const handleAssetSelect = (asset, isSelected) => {
        if (isSelected) {
            setSelectedAssets(prev => [...prev, asset]);
        } else {
            setSelectedAssets(prev => prev.filter(a => a.id !== asset.id));
            setSelectAll(false);
        }
    };

    // Handle select all toggle
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedAssets([]);
            setSelectAll(false);
        } else {
            setSelectedAssets([...assets]);
            setSelectAll(true);
        }
    };

    // Update selectAll state when individual assets are selected/deselected
    useEffect(() => {
        if (assets.length > 0 && selectedAssets.length === assets.length) {
            setSelectAll(true);
        } else {
            setSelectAll(false);
        }
    }, [selectedAssets, assets]);

    const handleLoadMore = () => {
        setCount(prevCount => prevCount + 100);
    };

    const handleClose = () => {
        setFileModal(false);
        setApiError('')
    }

    const handleImport = async (file) => {
        try {
            setLoading(true);
            setApiError('');

            if (!organizationId) {
                setApiError('Please select a organization first');
                return;
            }

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const assetsToImport = jsonData
                .map(item => {
                    const assetImageUrls = item['Asset Images'] 
                        ? item['Asset Images'].toString().split('\n').filter(url => url.trim() !== '')
                        : [];

                    return {
                        assetName: item['Name']?.toString() || '',
                        assetNumber: item['Asset ID']?.toString() || '',
                        serialNumber: item['Serial No.']?.toString() || '',
                        modelNumber: item['Model No.']?.toString() || '',
                        manufacturer: item['Manufacturer']?.toString() || '',
                        assetCategory: item['Category']?.toString() || '',
                        currentLocation: item['Current Location']?.toString() || '',
                        qrImageUrl: item['QR Payload']?.toString() || '',
                        assetImageUrls: assetImageUrls,
                        assetStatus: item['Asset Status']?.toString() || '',
                        vendorName: item['Vendor']?.toString() || '',
                        criticality: item['Criticality']?.toString() || '',
                        description: item['Description']?.toString() || '',
                        assetCost: parseFloat(item['Asset Cost']) || 0,
                        currency: item['Currency']?.toString() || '',
                        commissioningDate: item['Commissioning Date']?.toString() || '',
                        estimatedLifespan: parseInt(item['Estimated Lifespan']) || 0,
                        yearOfPurchase: item['Year of Purchase']?.toString() || '',
                        deviceClassification: item['Classification']?.toString() || '',
                        gmdnCode: item['GMDN Code']?.toString() || '',
                        cnehCode: item['CNEH Code']?.toString() || '',
                        organizationId: organizationId
                    };
                })
                .filter(asset => asset.assetName.trim() !== '');

            if (assetsToImport.length === 0) {
                setApiError('No valid asset data found in the file');
                return;
            }

            // Split assets into chunks of 1000
            const CHUNK_SIZE = 1000;
            const chunks = [];
            for (let i = 0; i < assetsToImport.length; i += CHUNK_SIZE) {
                chunks.push(assetsToImport.slice(i, i + CHUNK_SIZE));
            }

            try {
                let totalImported = 0;
                const errors = [];

                // Process each chunk sequentially
                for (let i = 0; i < chunks.length; i++) {
                    try {
                        await api.post('asset/create-by-admin', chunks[i]);
                        totalImported += chunks[i].length;
                    } catch (error) {
                        const errorMsg = error?.response?.data?.error || `Failed to import chunk ${i + 1}`;
                        errors.push(`Chunk ${i + 1} (rows ${i * CHUNK_SIZE + 1}-${i * CHUNK_SIZE + chunks[i].length}): ${errorMsg}`);
                    }
                }

                if (errors.length > 0) {
                    setApiError(`Imported ${totalImported} of ${assetsToImport.length} assets. Errors: ${errors.join('; ')}`);
                }

                await reloadAssets();
                setFileModal(false);
                return { success: errors.length === 0, count: totalImported };
            } catch (error) {
                const errorMsg = error?.response?.data?.error || "Failed to import assets";
                setApiError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            const errorMsg = error.message || "Error processing the file";
            setApiError(errorMsg);
            throw new Error(errorMsg);
        } finally {
            setLoading(false);
        }
    };
    
    const handleExport = () => {
        if (!assets || assets.length === 0) return;
        
        const dataToExport = assets.map((asset, index) => ({
            '#': index + 1,
            'Name': asset?.assetName || '',
            'Asset ID': asset?.assetNumber || '',
            'Asset Images': asset?.assetImageUrls?.join('\n') || '',
            'Serial No.': asset?.serialNumber || '',
            'Model No.': asset?.modelNumber || '',
            'Manufacturer': asset?.manufacturer || '',
            'Department': asset?.assetCategory || '',
            'QR Payload': asset?.qrImageUrl || 'Not Generated',
            'Current Location': asset?.currentLocation || '',
            'Asset Status': asset?.assetStatus || '',
            'Vendor': asset?.vendorName || '',
            'Criticality': asset?.criticality || '',
            'Description': asset?.description || '',
            'Asset Cost': asset?.assetCost || '',
            'Currency': asset?.currency || '',
            'Commissioning Date': asset?.commissioningDate || '',
            'Estimated Lifespan': asset?.estimatedLifespan || '',
            'Year of Purchase': asset?.yearOfPurchase || '',
            'Classification': asset?.deviceClassification || '',
            'GMDN Code': asset?.gmdnCode || '',
            'CNEH Code': asset?.cnehCode || ''
        }));
        
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Assets');
        
        XLSX.writeFile(wb, `assets_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const reloadAssets = useCallback(() => {
        setAssets([]);
        setCount(10);
        setHasMore(true);
        setSelectedAssets([]);
        setSelectAll(false);
        fetchAssetFromOrganization();
    }, [fetchAssetFromOrganization]);

    const fetchCategory = async () => {
        try {
            const { data } = await api.get("department/all");
            setDepartments(data.response)   
        } catch (error) {
            console.error("Error fetching enums:", error);
            return null;
        }
    };

    const openQrModal = (entry) => {
        setSelectedEntry(entry);
        setQrModal(true);
    };

    const downloadQRCode = async (imageUrl, assetName, serialNumber) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `QR_${assetName || 'Asset'}_${serialNumber || 'ID'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            setApiError('Failed to download QR code');
        }
    };

    const openImageViewer = (entry) => {
        setCurrentImages(entry?.assetImageUrls || []);
        setSelectedEntry(entry)
        setImageViewerModal(true);
    };

    const handleQr = async() => {
        try {
            setApiError('');
            const {data} = await api.get('get-qr');
            
            const updatedEntry = {
                ...selectedEntry,
                qrPayload: data.response.uuid,
                qrImageUrl: data.response.imageUrl
            };
            
            setSelectedEntry(updatedEntry);
            setQrGenerationSuccess(true);
            
            setTimeout(() => setQrGenerationSuccess(false), 3000);
            
            if (selectedEntry?.id) {
                const updatePayload = {
                    ...updatedEntry,
                    assetCost: parseFloat(updatedEntry?.assetCost) || 0,
                    estimatedLifespan: parseInt(updatedEntry?.estimatedLifespan) || 0
                };
                await api.put(`asset/update?assetId=${selectedEntry.id}`, updatePayload);
                await fetchAssetFromOrganization();
            }
        } catch (error) {
            console.error('QR generation failed:', error);
            setApiError('Failed to generate QR code');
        }
    }

    const deleteImageFromAsset = async (updatedImages) => {
        try {
            if (selectedEntry?.id) {
                await api.put(`asset/update?assetId=${selectedEntry.id}`, {
                    ...selectedEntry,
                    assetImageUrls: updatedImages,
                    currentLocation: selectedEntry?.locationId,
                    // Ensure all fields are properly included
                    assetCost: parseFloat(selectedEntry?.assetCost) || 0,
                    estimatedLifespan: parseInt(selectedEntry?.estimatedLifespan) || 0
                });
                await fetchAssetFromOrganization();
            }
            return true;
        } catch (error) {
            console.error("Failed to delete image:", error);
            setApiError(error?.response?.data?.error || "Failed to delete image");
            return false;
        }
    };

    const handleDeleteImage = (index) => {
        setDeleteConfirmation({
            show: true,
            index: index,
            imageUrl: currentImages[index],
            deleting: false
        });
    };

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
    
        try {
            const uploadedUrls = [];
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
               const publicURL = await getPresignedUrl(file);
                    uploadedUrls.push(publicURL);
            }
    
            setSelectedEntry(prev => ({
                ...prev,
                assetImageUrls: [...(prev.assetImageUrls || []), ...uploadedUrls]
            }));
    
        } catch (err) {
            setApiError("❌ Error during upload:", err);
        }
    };
    
   const getPresignedUrl = async (file) => {
  const formData = new FormData();

  formData.append("files", file);

  try {
    const response = await api.post("files/img-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const presignedUrl = response.data?.response[0]?.publicUrl;
    return presignedUrl;
  } catch (err) {
    console.error("Error getting presigned URL:", err);
    return null;
  }
};

    const handleOrganizationChange = (e) => {
        setOrganizationId(e.target.value);
        setAssets([]);
        setCount(10);
        setHasMore(true);
        // Clear selections when organization changes
        setSelectedAssets([]);
        setSelectAll(false);
    };

    const downloadAllQRCodes = async () => {
    try {
        setLoading(true);
        const assetsWithQR = assets.filter(asset => asset.qrImageUrl);
        
        if (assetsWithQR.length === 0) {
            setApiError('No QR codes available to download');
            return;
        }

        // Create a zip file using JSZip (you'll need to install: npm install jszip)
        const JSZip = require('jszip');
        const zip = new JSZip();
        
        // Download each QR code and add to zip
        let counter = 1;
        for (const asset of assetsWithQR) {
            try {
                const response = await fetch(asset.qrImageUrl);
                const blob = await response.blob();
                const fileName = `${counter}.png`; // 1.png, 2.png, 3.png ...
                zip.file(fileName, blob);
                counter++;
            } catch (error) {
                console.error(`Failed to download QR for ${asset.assetName}:`, error);
            }
        }

        // Generate and download the zip file
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipUrl = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `All_QR_Codes_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(zipUrl);

    } catch (error) {
        console.error('Bulk download failed:', error);
        setApiError('Failed to download QR codes');
    } finally {
        setLoading(false);
    }
};

    const tog_large = (entry = null) => {
        setmodal_large(!modal_large);
        
        if (entry) {
            // When editing, find the location and set up sublocations
            const currentLocation = locations?.find(loc => 
                loc.id == entry.locationId || 
                loc.subLocations?.some(sub => sub.id == entry.locationId)
            );
            
            let selectedLocationId = '';
            let subLocationId = '';
            
            if (currentLocation) {
                const isMainLocation = currentLocation.id == entry.locationId;
                if (isMainLocation) {
                    selectedLocationId = currentLocation.id;
                } else {
                    // It's a sublocation
                    selectedLocationId = currentLocation.id;
                    subLocationId = entry.locationId;
                }
                setSelectedLocationSubLocations(currentLocation.subLocations || []);
            }
            
            // Find vendor ID from vendor name if editing existing asset
            const vendorId = vendors.find(v => v.vendorName === entry.vendorName)?.id || '';

            setSelectedEntry({
                ...entry,
                selectedLocationId,
                subLocationId,
                vendorId
            });
        } else {
            setSelectedEntry({
                assetName: '',
                assetNumber: '',
                serialNumber: '',
                modelNumber: '',
                manufacturer: '',
                assetCategory: '',
                currentLocation: '',
                selectedLocationId: '',
                subLocationId: '',
                locationId: '',
                assetStatus: '',
                vendorId: '',
                vendorName: '',
                criticality: '',
                description: '',
                assetCost: '',
                currency: '',
                commissioningDate: '',
                estimatedLifespan: '',
                yearOfPurchase: '',
                deviceClassification: '',
                gmdnCode: '',
                cnehCode: ''
            });
            setSelectedLocationSubLocations([]);
        }
        
        setErrors({});
        setApiError('');
    };

    const handleModalClose = () => {
        setmodal_large(false);
        setSelectedEntry(null);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        
        const newErrors = {};
        if (!selectedEntry?.assetName?.trim()) newErrors.assetName = 'Asset name is required';
        if (!selectedEntry?.assetNumber?.trim()) newErrors.assetNumber = 'Asset ID is required';
        if (!selectedEntry?.serialNumber?.trim()) newErrors.serialNumber = 'Serial number is required';
        if (!selectedEntry?.modelNumber?.trim()) newErrors.modelNumber = 'Model number is required';
        if (!selectedEntry?.manufacturer?.trim()) newErrors.manufacturer = 'Manufacturer is required';
        if (!selectedEntry?.assetCategory?.trim()) newErrors.assetCategory = 'Department is required';
        if (!selectedEntry?.locationId) newErrors.currentLocation = 'Location is required';

        // New field validations
        if (!selectedEntry?.vendorId) newErrors.vendorId = 'Vendor is required';
        if (!selectedEntry?.criticality?.trim()) newErrors.criticality = 'Criticality is required';
        if (!selectedEntry?.description?.trim()) newErrors.description = 'Description is required';
        if (!selectedEntry?.assetStatus?.trim()) newErrors.assetStatus = 'Asset Status is required';
        if (!selectedEntry?.assetCost || selectedEntry.assetCost <= 0) newErrors.assetCost = 'Asset cost must be greater than 0';
        if (!selectedEntry?.currency || selectedEntry?.currency.trim() === '') newErrors.currency = 'Currency is required';
        if (!selectedEntry?.commissioningDate?.trim()) newErrors.commissioningDate = 'Commissioning date is required';
        if (!selectedEntry?.estimatedLifespan || selectedEntry.estimatedLifespan <= 0) newErrors.estimatedLifespan = 'Estimated lifespan must be greater than 0';
        if (!selectedEntry?.yearOfPurchase) newErrors.yearOfPurchase = 'Year of purchase is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setApiError('');
            
            const payload = {
                ...selectedEntry,
                organizationId: organizationId,
                currentLocation: selectedEntry?.locationId,
                // Ensure all new fields are included
                assetNumber: selectedEntry?.assetNumber,
                assetStatus: selectedEntry?.assetStatus,
                vendorId: selectedEntry?.vendorId,
                criticality: selectedEntry?.criticality,
                description: selectedEntry?.description,
                assetCost: parseFloat(selectedEntry?.assetCost) || 0,
                currency: selectedEntry?.currency,
                commissioningDate: selectedEntry?.commissioningDate,
                estimatedLifespan: parseInt(selectedEntry?.estimatedLifespan) || 0,
                yearOfPurchase: selectedEntry?.yearOfPurchase,
                deviceClassification: selectedEntry?.deviceClassification,
                gmdnCode: selectedEntry?.gmdnCode,
                cnehCode: selectedEntry?.cnehCode
            };

            if (selectedEntry?.id) {
                await api.put(`asset/update?assetId=${selectedEntry?.id}`, payload);
            } else {
                await api.post('asset/create-by-admin', [payload]);
            }

            await reloadAssets()
            handleModalClose();
        } catch (error) {
            setApiError(error?.response?.data?.error || "Something went wrong!");
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`asset?assetId=${selectedEntry?.id}`);
            await reloadAssets()
            setDeleteModal(false);
        } catch (error) {
            console.error('Error deleting asset:', error);
            setApiError('Failed to delete asset');
        }
    }

    const clearFilters = () => {
        setFilters({
            department: '',
            location: '',
            manufacturer: ''
        });
        setFilterModal(false);
    };

    // Handle transfer completion
    const handleTransferComplete = () => {
        setSelectedAssets([]);
        setSelectAll(false);
        reloadAssets();
    };

    useEffect(() => {
        fetchOrganizations();
        fetchCategory();
        fetchManufacturer();
        fetchCriticality();
        fetchEnums();
        fetchVendors();
    }, []);

    useEffect(() => {
        if(organizationId)
        fetchLocations()
    },[organizationId])

    useEffect(() => {
        fetchAssetFromOrganization();
    }, [fetchAssetFromOrganization]);

    return (
        <div className='page-content mt-4'>
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="asset:create">
                        <button
                            type="button"
                            className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
                            onClick={() => tog_large()}
                        >
                            <FeatherIcon icon="plus" />
                            Add Asset
                        </button>
                    </PermissionGuard>

                    {/* Transfer Button */}
                    <PermissionGuard permission="asset:update">
                        <button
                            type="button"
                            className="btn btn-soft-warning waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
                            onClick={() => setTransferModal(true)}
                            disabled={selectedAssets.length === 0}
                            title={selectedAssets.length === 0 ? 'Select assets to transfer' : `Transfer ${selectedAssets.length} selected asset${selectedAssets.length !== 1 ? 's' : ''}`}
                        >
                            <FeatherIcon icon="send" />
                            Transfer ({selectedAssets.length})
                        </button>
                    </PermissionGuard>

                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder="Search Assets..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>
            
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">Assets</h3>
                        <div className="d-flex align-items-center gap-2">
                           <button
                            className="btn btn-soft-primary p-2"
                            onClick={downloadAllQRCodes}
                            title="Download QR"
                            >
                            <FeatherIcon icon="download" size={18} />
                            </button>
                            <button
                                className="btn btn-soft-primary p-2"
                                onClick={() => setFilterModal(true)}
                                title="Filter assets"
                            >
                                <FeatherIcon icon="filter" size={18} />
                            </button>
                            <PermissionGuard permission="asset:create">
                                <button
                                    className="btn btn-soft-info p-2"
                                    onClick={() => setFileModal(true)}
                                    title="Import/Export transactions"
                                >
                                    <FeatherIcon icon="file-text" size={18} />
                                </button>
                            </PermissionGuard>
                            <select 
                                className="form-select form-select-md w-auto" 
                                value={organizationId || ''}
                                onChange={handleOrganizationChange}
                            >
                                <option value="" disabled>Select Organization</option>
                                {organizations?.map((organization) => (
                                    <option key={organization.id} value={organization.id}>
                                        {organization?.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </CardHeader>
                
                <CardBody>
                    <div 
                        className="table-responsive" 
                        style={{ 
                            overflowX: 'auto', 
                            whiteSpace: 'nowrap',
                             maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{ 
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 1,
                                }}> 
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            className="form-check-input"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </th>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Asset ID</th>
                                    <th>Images</th>
                                    <th>Serial No.</th>
                                    <th>Model No.</th>
                                    <th>Manufacturer</th>
                                    <th>Department</th>
                                    <th>Asset Status</th>
                                    <th>Vendor</th>
                                    <th>Criticality</th>
                                    <th>Asset Cost</th>
                                    <th>Commissioning Date</th>
                                    <th>Estimated Lifespan</th>
                                    <th>Classification</th>
                                    <th>GMDN Code</th>
                                    <th>CNEH Code</th>
                                    <th>Qr Payload</th>
                                    <th>Current Location</th>
                                    <th>Edit</th>
                                    <th>Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                {assets?.map((asset, ind) => {
                                    const isSelected = selectedAssets.some(a => a.id === asset.id);
                                    return (
                                        <motion.tr
                                            key={asset?.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                            style={{
                                                backgroundColor: isSelected ? '#f8f9fa' : 'transparent'
                                            }}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => handleAssetSelect(asset, e.target.checked)}
                                                    className="form-check-input"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </td>
                                            <th scope="row">{ind+1}</th>
                                            <td>{asset?.assetName}</td>
                                            <td>{asset?.assetNumber}</td>
                                             <td className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-soft-success waves-effect waves-light"
                                                    onClick={() => openImageViewer(asset)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                            <td>{asset?.serialNumber}</td>
                                            <td>{asset?.modelNumber}</td>
                                            <td>{asset?.manufacturer}</td>
                                            <td>{asset?.assetCategory}</td>
                                            <td>
                                                <span className="badge bg-primary">{asset?.assetStatus}</span>
                                            </td>
                                            <td>{asset?.vendorName}</td>
                                            <td>
                                                <span className={`badge ${
                                                    asset?.criticality === 'HIGH' ? 'bg-danger' :
                                                    asset?.criticality === 'MEDIUM' ? 'bg-warning text-dark' :
                                                    asset?.criticality === 'LOW' ? 'bg-success' :
                                                    'bg-secondary'
                                                }`}>
                                                    {asset?.criticality}
                                                </span>
                                            </td>
                                            <td>{asset?.assetCost ? `${asset?.currency} ${asset?.assetCost}` : 'N/A'}</td>
                                            <td>{asset?.commissioningDate || 'N/A'}</td>
                                            <td>{asset?.estimatedLifespan ? `${asset?.estimatedLifespan} years` : 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${
                                                    asset?.deviceClassification === 'CLASS_III' ? 'bg-danger' :
                                                    asset?.deviceClassification === 'CLASS_IIB' ? 'bg-warning text-dark' :
                                                    asset?.deviceClassification === 'CLASS_IIA' ? 'bg-info' :
                                                    asset?.deviceClassification === 'CLASS_I' ? 'bg-success' :
                                                    'bg-secondary'
                                                }`}>
                                                    {asset?.deviceClassification || 'N/A'}
                                                </span>
                                            </td>
                                            <td>{asset?.gmdnCode || 'N/A'}</td>
                                            <td>{asset?.cnehCode || 'N/A'}</td>
                                            <td className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-soft-success waves-effect waves-light"
                                                    onClick={() => openQrModal(asset)}
                                                    disabled={!asset.qrImageUrl}
                                                >
                                                    {asset.qrImageUrl ? 'View' : 'Not Generated'}
                                                </button>
                                            </td>
                                            <td>{asset.currentLocation}</td>
                                            <PermissionGuard permission="asset:update">
                                                <td className='text-warning' role='button' onClick={() => tog_large(asset)}>
                                                    <FeatherIcon icon="edit" />
                                                </td>
                                            </PermissionGuard>
                                            <PermissionGuard permission="asset:delete">
                                                <td className='text-danger' role='button' onClick={() => {
                                                    setSelectedEntry(asset);
                                                    setDeleteModal(true);
                                                }}>
                                                    <FeatherIcon icon="trash-2" />
                                                </td>
                                            </PermissionGuard>
                                        </motion.tr>
                                    );
                                })}
                                </AnimatePresence>
                            </tbody>
                        </table>
                        {loading && (
                            <div className="text-center py-3">
                                <Spinner color="primary" />
                            </div>
                        )}
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                            {hasMore ? (
                                <Button 
                                    color="primary" 
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                >
                                    {loading ? 'Loading...' : 'Load More'}
                                </Button>
                            ) : (
                                <div className="text-muted py-2">
                                    {assets.length === 0 ? 
                                        (searchTerm ? 'No assets match your search' : 'No assets found') : 
                                        'No more assets to load'}
                                </div>
                            )}
                        </div>
                </CardBody>
            </Card>

            {/* Transfer Modal */}
            <TransferModal
                isOpen={transferModal}
                toggle={() => setTransferModal(false)}
                selectedAssets={selectedAssets}
                onTransferComplete={handleTransferComplete}
                currentOrganizationId={organizationId}
            />

            {/* Filter Modal */}
            <Modal
                isOpen={filterModal}
                toggle={() => setFilterModal(false)}
                centered
                className="fade-scale-modal"
                backdropClassName="modal-backdrop-blur"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header text-white">
                        <h5 className="modal-title fs-4">Filter Assets</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setFilterModal(false)}
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body p-4">
                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <div className="form-group">
                                    <label htmlFor="filterDepartment" className="form-label fw-medium">
                                        Department
                                    </label>
                                    <select
                                        id="filterDepartment"
                                        className="form-select"
                                        value={filters.department}
                                        onChange={(e) =>
                                            setFilters({ ...filters, department: e.target.value })
                                        }
                                    >
                                        <option value="">All Departments</option>
                                        {departments?.map((department) => (
                                            <option key={department.id} value={department?.departmentName}>
                                                {department?.departmentName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <div className="form-group">
                                    <label htmlFor="filterLocation" className="form-label fw-medium">
                                        Location
                                    </label>
                                    <select
                                        id="filterLocation"
                                        className="form-select"
                                        value={filters.location}
                                        onChange={(e) =>
                                            setFilters({ ...filters, location: e.target.value })
                                        }
                                    >
                                        <option value="">All Locations</option>
                                        {locations?.map((location) => (
                                            <option key={location.id} value={location.locationName}>
                                                {location.locationName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <div className="form-group">
                                    <label htmlFor="filterManufacturer" className="form-label fw-medium">
                                        Manufacturer
                                    </label>
                                    <select
                                        id="filterManufacturer"
                                        className="form-select"
                                        value={filters.manufacturer}
                                        onChange={(e) =>
                                            setFilters({ ...filters, manufacturer: e.target.value })
                                        }
                                    >
                                        <option value="">All Manufacturer</option>
                                        {manufacturer?.map((item) => (
                                            <option key={item} value={item}>
                                                {item}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="modal-footer bg-light border-top-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setFilterModal(false)}
                        >
                            Apply Filters
                        </button>
                    </div>
                </motion.div>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal size="lg" isOpen={modal_large} toggle={() => tog_large()}>
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title" id="myLargeModalLabel">
                            {selectedEntry?.id ? 'Edit Asset' : 'Add Asset'}
                        </h5>
                        <button onClick={handleModalClose} type="button" className="btn-close" aria-label="Close"></button>
                    </div>
                    
                    <div className="modal-body">
                        <form onSubmit={handleUpdate}>
                            <Row className="mb-4">
                                <label htmlFor="assetName" className="col-sm-3 col-form-label">Name</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="assetName"
                                        className={`form-control ${errors.assetName ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.assetName || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, assetName: e.target.value})}
                                    />
                                    {errors.assetName && <small className="text-danger">{errors.assetName}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="assetNumber" className="col-sm-3 col-form-label">Asset ID</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="assetNumber"
                                        className={`form-control ${errors.assetNumber ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.assetNumber || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, assetNumber: e.target.value})}
                                    />
                                    {errors.assetNumber && <small className="text-danger">{errors.assetNumber}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="serialNumber" className="col-sm-3 col-form-label">Serial No.</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="serialNumber"
                                        className={`form-control ${errors.serialNumber ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.serialNumber || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, serialNumber: e.target.value})}
                                    />
                                    {errors.serialNumber && <small className="text-danger">{errors.serialNumber}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="manufacturer" className="col-sm-3 col-form-label">Manufacturer</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="manufacturer"
                                        className={`form-control ${errors.manufacturer ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.manufacturer || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, manufacturer: e.target.value})}
                                    />
                                    {errors.manufacturer && <small className="text-danger">{errors.manufacturer}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="assetCategory" className="col-sm-3 col-form-label">Department</label>
                                <Col sm={9}>
                                    <select
                                        id="category"
                                        className={`form-select ${errors.assetCategory ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.assetCategory || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, assetCategory: e.target.value})}
                                    >
                                        <option value="">Select Department</option>
                                        {departments?.map((department) => (
                                            <option key={department.id} value={department?.departmentName}>
                                                {department?.departmentName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.assetCategory && <small className="text-danger">{errors.assetCategory}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="assetStatus" className="col-sm-3 col-form-label">Asset Status</label>
                                <Col sm={9}>
                                    <select
                                        id="assetStatus"
                                        className={`form-select ${errors.assetStatus ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.assetStatus || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, assetStatus: e.target.value })}
                                    >
                                        <option value="">Select Status</option>
                                        {groupedAssetStatusOptions.map((group) => (
                                            <optgroup key={group.group} label={group.group}>
                                                {group.options.map((status) => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                    {errors.assetStatus && <small className="text-danger">{errors.assetStatus}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="manufacturer" className="col-sm-3 col-form-label">Model Number</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="modelNumber"
                                        className={`form-control ${errors.modelNumber ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.modelNumber || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, modelNumber: e.target.value})}
                                    />
                                    {errors.modelNumber && <small className="text-danger">{errors.modelNumber}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="horizontal-name-input" className="col-sm-3 col-form-label">Asset Images</label>
                                <Col sm={9}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="form-control"
                                        id="horizontal-name-input"
                                        multiple 
                                    />
                                    {errors.qr && <small className="text-danger">{errors.qr}</small>}
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <label htmlFor="horizontal-name-input" className="col-sm-3 col-form-label">QR Code</label>
                                <Col sm={9}>
                                    <div className="d-flex align-items-center gap-3">
                                        <button 
                                            type="button" 
                                            onClick={handleQr} 
                                            className="btn btn-success btn-rounded waves-effect waves-light"
                                        >
                                            {selectedEntry?.qrImageUrl ? 'Regenerate QR' : 'Generate QR'}
                                        </button>
                                        
                                        {qrGenerationSuccess && (
                                            <Alert color="success" className="mb-0 py-1 px-2">
                                                QR generated successfully!
                                            </Alert>
                                        )}
                                        
                                        {(selectedEntry?.qrPayload || selectedEntry?.qrImageUrl) && (
                                            <div 
                                                className="position-relative"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setQrModal(true);
                                                    setQrGenerationSuccess(false);
                                                }}
                                            >
                                                <img 
                                                    src={selectedEntry.qrImageUrl || `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${selectedEntry.qrPayload}`} 
                                                    alt="QR Preview" 
                                                    style={{ 
                                                        width: '50px', 
                                                        height: '50px',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '4px'
                                                    }}
                                                />
                                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                                                    style={{ 
                                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                                        borderRadius: '4px',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        ':hover': {
                                                            opacity: 1
                                                        }
                                                    }}
                                                >
                                                    <FeatherIcon icon="maximize-2" size={16} color="white" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.qr && <small className="text-danger">{errors.qr}</small>}
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <label htmlFor="currentLocation" className="col-sm-3 col-form-label">Current Location</label>
                                <Col sm={9}>
                                    <select
                                        id="currentLocation"
                                        className={`form-select ${errors.currentLocation ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.selectedLocationId || ''}
                                        onChange={(e) => {
                                            const selectedLocationId = e.target.value;
                                            const selectedLocation = locations?.find(loc => loc.id == selectedLocationId);
                                            setSelectedLocationSubLocations(selectedLocation?.subLocations || []);
                                            setSelectedEntry({
                                                ...selectedEntry, 
                                                selectedLocationId: selectedLocationId,
                                                locationId: selectedLocationId,
                                                subLocationId: '' // Reset sublocation when location changes
                                            });
                                        }}
                                    >
                                        <option value="">Select Location</option>
                                        {locations?.map((location) => (
                                            <option key={location.id} value={location.id}>
                                                {location.locationName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.currentLocation && <small className="text-danger">{errors.currentLocation}</small>}
                                </Col>
                            </Row>

                            {/* Show message if location has no sublocations */}
                            {selectedEntry?.selectedLocationId && selectedLocationSubLocations.length === 0 && (
                                <Row className="mb-3">
                                    <Col sm={9} className="offset-sm-3">
                                        <span className="text-muted small">
                                            The current location does not have any sublocation
                                        </span>
                                    </Col>
                                </Row>
                            )}

                            {/* Sublocation dropdown - only show if selected location has sublocations */}
                            {selectedLocationSubLocations.length > 0 && (
                                <Row className="mb-4">
                                    <label htmlFor="subLocation" className="col-sm-3 col-form-label">Sublocation</label>
                                    <Col sm={9}>
                                        <select
                                            id="subLocation"
                                            className="form-select"
                                            value={selectedEntry?.subLocationId || ''}
                                            onChange={(e) => {
                                                const subLocationId = e.target.value;
                                                setSelectedEntry({
                                                    ...selectedEntry,
                                                    subLocationId: subLocationId,
                                                    // If sublocation is selected, use its ID as locationId, otherwise use main location ID
                                                    locationId: subLocationId || selectedEntry?.selectedLocationId
                                                });
                                            }}
                                        >
                                            <option value="">Select Sublocation</option>
                                            {selectedLocationSubLocations.map((sublocation) => (
                                                <option key={sublocation.id} value={sublocation.id}>
                                                    {sublocation.locationName}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                </Row>
                            )}

                            <Row className="mb-4">
                                <label htmlFor="yearOfPurchase" className="col-sm-3 col-form-label">Year of Purchase</label>
                                <Col sm={9}>
                                    <input
                                        type="number"
                                        id="yearOfPurchase"
                                        className={`form-control ${errors.yearOfPurchase ? 'is-invalid' : ''}`}
                                        min="1900"
                                        value={selectedEntry?.yearOfPurchase || ''}
                                        max={new Date().getFullYear()}
                                        placeholder="Enter year"
                                        onChange={(e) => setSelectedEntry({...selectedEntry, yearOfPurchase: e.target.value})}
                                    />
                                    {errors.yearOfPurchase && <small className="text-danger">{errors.yearOfPurchase}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="vendor" className="col-sm-3 col-form-label">Vendor</label>
                                <Col sm={9}>
                                    <select
                                        id="vendor"
                                        className={`form-select ${errors.vendorId ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.vendorId || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, vendorId: e.target.value })}
                                    >
                                        <option value="">Select Vendor</option>
                                        {vendors?.map((vendor) => (
                                            <option key={vendor.id} value={vendor.id}>
                                                {vendor.vendorName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.vendorId && <small className="text-danger">{errors.vendorId}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="criticality" className="col-sm-3 col-form-label">Criticality</label>
                                <Col sm={9}>
                                    <select
                                        id="criticality"
                                        className={`form-select ${errors.criticality ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.criticality || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, criticality: e.target.value})}
                                    >
                                        <option value="">Select Criticality</option>
                                        {criticals?.map((critical) => (
                                            <option key={critical} value={critical}>
                                                {critical}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.criticality && <small className="text-danger">{errors.criticality}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="description" className="col-sm-3 col-form-label">Description</label>
                                <Col sm={9}>
                                    <textarea
                                        id="description"
                                        rows="3"
                                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.description || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, description: e.target.value })}
                                    />
                                    {errors.description && <small className="text-danger">{errors.description}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="assetCost" className="col-sm-3 col-form-label">Asset Cost</label>
                                <Col sm={6}>
                                    <input
                                        type="number"
                                        id="assetCost"
                                        step="0.01"
                                        className={`form-control ${errors.assetCost ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.assetCost || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, assetCost: e.target.value })}
                                        placeholder="Enter asset cost"
                                    />
                                    {errors.assetCost && <small className="text-danger">{errors.assetCost}</small>}
                                </Col>
                                <Col sm={3}>
                                    <select
                                        id="currency"
                                        className={`form-select ${errors.currency ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.currency || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, currency: e.target.value })}
                                    >
                                        <option value="">Select Currency</option>
                                        {Object.entries(currencies).map(([code, label]) => (
                                            <option key={code} value={code}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.currency && <small className="text-danger">{errors.currency}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="commissioningDate" className="col-sm-3 col-form-label">Commissioning Date</label>
                                <Col sm={9}>
                                    <input
                                        type="date"
                                        id="commissioningDate"
                                        className={`form-control ${errors.commissioningDate ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.commissioningDate ? (() => {
                                            // Convert DD-MM-YYYY to YYYY-MM-DD for date input display
                                            const dateStr = selectedEntry.commissioningDate.toString();
                                            if (dateStr.includes('-') && dateStr.split('-').length === 3) {
                                                const parts = dateStr.split('-');
                                                // Check if it's DD-MM-YYYY format (day is 2 digits and less than 32)
                                                if (parts[0].length <= 2 && parseInt(parts[0]) <= 31 && parseInt(parts[0]) >= 1) {
                                                    // DD-MM-YYYY format, convert to YYYY-MM-DD
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                } else if (parts[0].length === 4) {
                                                    // Already in YYYY-MM-DD format
                                                    return dateStr;
                                                }
                                            }
                                            // If it's not a recognizable date format, try to parse as ISO
                                            try {
                                                const date = new Date(dateStr);
                                                if (!isNaN(date.getTime())) {
                                                    return date.toISOString().split('T')[0];
                                                }
                                            } catch (e) {
                                                // Ignore parsing errors
                                            }
                                            return dateStr;
                                        })() : ''}
                                        onChange={(e) => {
                                            // Convert YYYY-MM-DD to DD-MM-YYYY for storage
                                            const dateValue = e.target.value;
                                            if (dateValue) {
                                                const [year, month, day] = dateValue.split('-');
                                                const formattedDate = `${day}-${month}-${year}`;
                                                setSelectedEntry({ ...selectedEntry, commissioningDate: formattedDate});
                                            } else {
                                                setSelectedEntry({ ...selectedEntry, commissioningDate: ''});
                                            }
                                        }}
                                    />
                                    {errors.commissioningDate && <small className="text-danger">{errors.commissioningDate}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label htmlFor="estimatedLifespan" className="col-sm-3 col-form-label">Estimated Lifespan (Years)</label>
                                <Col sm={9}>
                                    <input
                                        type="number"
                                        id="estimatedLifespan"
                                        min="1"
                                        className={`form-control ${errors.estimatedLifespan ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.estimatedLifespan || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, estimatedLifespan: e.target.value })}
                                        placeholder="Enter estimated lifespan in years"
                                    />
                                    {errors.estimatedLifespan && <small className="text-danger">{errors.estimatedLifespan}</small>}
                                </Col>
                            </Row>

                            {/* Classification */}
                            <Row className="mb-4">
                                <label htmlFor="deviceClassification" className="col-sm-3 col-form-label">Classification</label>
                                <Col sm={9}>
                                    <select
                                        id="deviceClassification"
                                        className={`form-select ${errors.deviceClassification ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.deviceClassification || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, deviceClassification: e.target.value })}
                                    >
                                        <option value="">Select Classification</option>
                                        <option value="CLASS_I">CLASS I</option>
                                        <option value="CLASS_IIA">CLASS IIA</option>
                                        <option value="CLASS_IIB">CLASS IIB</option>
                                        <option value="CLASS_III">CLASS III</option>
                                    </select>
                                    {errors.deviceClassification && <small className="text-danger">{errors.deviceClassification}</small>}
                                </Col>
                            </Row>

                            {/* GMDN Code */}
                            <Row className="mb-4">
                                <label htmlFor="gmdnCode" className="col-sm-3 col-form-label">GMDN Code</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="gmdnCode"
                                        className={`form-control ${errors.gmdnCode ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.gmdnCode || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, gmdnCode: e.target.value })}
                                        placeholder="Enter GMDN code"
                                    />
                                    {errors.gmdnCode && <small className="text-danger">{errors.gmdnCode}</small>}
                                </Col>
                            </Row>

                            {/* CNEH Code */}
                            <Row className="mb-4">
                                <label htmlFor="cnehCode" className="col-sm-3 col-form-label">CNEH Code</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="cnehCode"
                                        className={`form-control ${errors.cnehCode ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.cnehCode || ''}
                                        onChange={(e) => setSelectedEntry({ ...selectedEntry, cnehCode: e.target.value })}
                                        placeholder="Enter CNEH code"
                                    />
                                    {errors.cnehCode && <small className="text-danger">{errors.cnehCode}</small>}
                                </Col>
                            </Row>

                            <div className="mt-4">
                                {apiError && <div className="alert alert-danger">{apiError}</div>}
                                <button type="submit" className="btn btn-success w-lg transition-all hover-grow pulse-on-hover">
                                    {selectedEntry?.id ? 'Update' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </Modal>

            {/* Image Viewer Modal */}
            <Modal isOpen={imageViewerModal} toggle={() => setImageViewerModal(false)} size="lg">
                <div className="modal-header">
                    <h5 className="modal-title">Asset Images</h5>
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setImageViewerModal(false)}
                        aria-label="Close"
                    ></button>
                </div>
                <div className="modal-body">
                    {currentImages.length > 0 ? (
                        <Row>
                            {currentImages.map((imageUrl, index) => (
                                <Col md={6} className="mb-4" key={index}>
                                    <div className="card border-0 shadow-sm">
                                        <div className="position-relative">
                                            <img 
                                                src={imageUrl} 
                                                alt={`Asset ${index + 1}`} 
                                                className="img-fluid rounded-top"
                                                style={{ 
                                                    objectFit: 'cover',
                                                    height: '200px',
                                                    width: '100%'
                                                }}
                                            />
                                            <div className="position-absolute top-0 end-0 p-2">
                                                <button
                                                    className="btn btn-danger btn-sm rounded-circle"
                                                    onClick={() => handleDeleteImage(index)}
                                                    title="Delete image"
                                                    style={{
                                                        width: '30px',
                                                        height: '30px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <FeatherIcon icon="trash-2" size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted">Image {index + 1}</small>
                                                <a 
                                                    href={imageUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    View Full
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <div className="text-center py-4">
                            <FeatherIcon icon="image" size="48" className="text-muted mb-2" />
                            <p className="text-muted">No images available for this asset</p>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setImageViewerModal(false)}
                    >
                        Close
                    </button>
                </div>
            </Modal>

            <Modal isOpen={deleteConfirmation.show} toggle={() => !deleteConfirmation.deleting && setDeleteConfirmation(prev => ({...prev, show: false }))} centered>
                <div className="modal-header border-0 pb-0">
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => !deleteConfirmation.deleting && setDeleteConfirmation(prev => ({...prev, show: false }))}
                        aria-label="Close"
                        disabled={deleteConfirmation.deleting}
                    ></button>
                </div>
                <div className="modal-body text-center py-0">
                    <FeatherIcon icon="alert-triangle" size="48" className="text-warning mb-3" />
                    <h5 className="mb-3">Delete this image?</h5>
                    
                    <div className="mb-4 position-relative" style={{ minHeight: '150px' }}>
                        <img 
                            src={deleteConfirmation.imageUrl} 
                            alt="Preview" 
                            className="img-thumbnail rounded"
                            style={{ 
                                maxHeight: '150px',
                                opacity: deleteConfirmation.deleting ? 0.5 : 1
                            }}
                        />
                        {deleteConfirmation.deleting && (
                            <div className="position-absolute top-50 start-50 translate-middle">
                                <div className="spinner-border text-danger" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <p className="text-muted mb-4">
                        This image will be permanently removed from this asset.
                    </p>
                </div>
                <div className="modal-footer border-0 pt-0 justify-content-center">
                    <button 
                        type="button" 
                        className="btn btn-light" 
                        onClick={() => setDeleteConfirmation(prev => ({...prev, show: false }))}
                        disabled={deleteConfirmation.deleting}
                        style={{ minWidth: '100px' }}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger ms-2" 
                        disabled={deleteConfirmation.deleting}
                        onClick={async () => {
                            setDeleteConfirmation(prev => ({...prev, deleting: true }));
                            
                            const updatedImages = [...currentImages];
                            updatedImages.splice(deleteConfirmation.index, 1);
                            
                            const success = await deleteImageFromAsset(updatedImages);
                            
                            if (success) {
                                setCurrentImages(updatedImages);
                                setSelectedEntry(prev => ({
                                    ...prev,
                                    assetImageUrls: updatedImages
                                }));
                            }
                            
                            setDeleteConfirmation(prev => ({...prev, show: false, deleting: false }));
                        }}
                        style={{ minWidth: '100px' }}
                    >
                        {deleteConfirmation.deleting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </button>
                </div>
            </Modal>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title">Confirm Delete</h5>
                    <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                    <p>Are you sure you want to delete this asset? This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger" 
                        onClick={handleDelete}
                    >
                        Delete
                    </button>
                </div>
            </Modal>

            {/* QR Code Viewer Modal */}
            <Modal isOpen={qrModal} toggle={() => setQrModal(false)} size="md">
                <div className="modal-header">
                    <h5 className="modal-title">QR Code</h5>
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setQrModal(false)}
                        aria-label="Close"
                    ></button>
                </div>
                <div className="modal-body text-center">
                    {selectedEntry?.qrImageUrl ? (
                        <div className="card border-0 shadow-sm">
                            <div className="position-relative">
                                <img 
                                    src={selectedEntry.qrImageUrl} 
                                    alt="QR Code" 
                                    className="img-fluid rounded-top"
                                    style={{ 
                                        width: '100%',
                                        maxHeight: '300px',
                                        objectFit: 'contain'
                                    }}
                                />
                                <div className="position-absolute top-0 end-0 p-2">
                                    <button
                                        onClick={() => downloadQRCode(
                                            selectedEntry.qrImageUrl,
                                            selectedEntry.assetName,
                                            selectedEntry.serialNumber
                                        )}
                                        className="btn btn-primary btn-sm rounded-circle"
                                        title="Download QR Code"
                                        style={{
                                            width: '30px',
                                            height: '30px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <FeatherIcon icon="download" size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="card-body p-2">
                                <p className="mb-1"><strong>Asset:</strong> {selectedEntry.assetName}</p>
                                <p className="mb-1"><strong>Serial No:</strong> {selectedEntry.serialNumber}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <FeatherIcon icon="alert-circle" size="48" className="text-warning mb-2" />
                            <p className="text-muted">No QR code generated for this asset</p>
                            <button 
                                type="button" 
                                className="btn btn-success mt-2"
                                onClick={handleQr}
                            >
                                Generate QR Code
                            </button>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setQrModal(false)}
                    >
                        Close
                    </button>
                </div>
            </Modal>

            <FileModal 
                title='Transactions'
                isOpen={fileModal} 
                toggle={handleClose} 
                data={assets}
                onImport={handleImport}
                onExport={handleExport}
                error={apiError}
                loading={loading}
                importLabel="Import Assets from Excel"
                exportLabel="Export Assets to Excel"
            />
        </div>
    );
};

export default Assets;